// components/tracking/DeliveryProgressBar.jsx
import React from 'react';
import { FaCheck, FaBox, FaTruck, FaMapMarkerAlt, FaHome } from 'react-icons/fa';

const DeliveryProgressBar = ({ trackingHistory, latestStatus }) => {
  // Define the standard delivery stages
  const deliveryStages = [
    { 
      id: 'ordered', 
      label: 'Order Placed', 
      icon: FaBox,
      codes: ['30', '270'],
      color: 'blue'
    },
    { 
      id: 'picked', 
      label: 'Picked Up', 
      icon: FaTruck,
      codes: ['60'],
      color: 'orange'
    },
    { 
      id: 'transit', 
      label: 'In Transit', 
      icon: FaMapMarkerAlt,
      codes: ['70', '200'],
      color: 'purple'
    },
    { 
      id: 'delivery', 
      label: 'Out for Delivery', 
      icon: FaTruck,
      codes: ['80'],
      color: 'indigo'
    },
    { 
      id: 'delivered', 
      label: 'Delivered', 
      icon: FaHome,
      codes: ['100'],
      color: 'green'
    }
  ];

  // Check if current status is RTO
  const isRTOStatus = (latestStatus?.order_status_code && [
    '130', '140', '150', '160', '170', '230', '250', '260'
  ].includes(latestStatus.order_status_code.toString())) || 
  (latestStatus?.order_status_description?.toLowerCase().includes('rto'));

  // Get current stage index based on tracking history
  const getCurrentStageIndex = () => {
    if (!trackingHistory || trackingHistory.length === 0) return -1;
    
    // Sort by most recent first
    const sortedHistory = [...trackingHistory].sort(
      (a, b) => new Date(b.order_status_date_time_ist) - new Date(a.order_status_date_time_ist)
    );

    // Check each stage from the end (delivered) to beginning
    for (let i = deliveryStages.length - 1; i >= 0; i--) {
      const stage = deliveryStages[i];
      const hasStageCode = sortedHistory.some(item => 
        stage.codes.includes(item.order_status_code?.toString())
      );
      if (hasStageCode) return i;
    }
    return 0; // Default to first stage if no codes match
  };

  const currentStageIndex = getCurrentStageIndex();
  const progressPercentage = currentStageIndex >= 0 ? ((currentStageIndex + 1) / deliveryStages.length) * 100 : 0;

  // Get stage date
  const getStageDate = (stageCodes) => {
    if (!trackingHistory) return null;
    
    const stageItem = trackingHistory.find(item => 
      stageCodes.includes(item.order_status_code?.toString())
    );
    
    if (stageItem) {
      return new Date(stageItem.order_status_date_time_ist).toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short'
      });
    }
    return null;
  };

  if (isRTOStatus) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-orange-100 rounded-lg">
            <FaTruck className="text-orange-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Return to Origin (RTO)</h3>
            <p className="text-sm text-gray-600">This shipment is being returned to sender</p>
          </div>
        </div>
        
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-orange-800">
            <span className="text-2xl">↩️</span>
            <div>
              <p className="font-semibold">Package Return in Progress</p>
              <p className="text-sm text-orange-700">Your package is being sent back to the original sender due to delivery issues.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-blue-100 rounded-lg">
          <FaTruck className="text-blue-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Delivery Progress</h3>
          <p className="text-sm text-gray-600">Track your shipment journey</p>
        </div>
      </div>
      
      {/* Progress Bar */}
      <div className="relative mb-8">
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div 
            className="bg-gradient-to-r from-blue-500 to-green-500 h-3 rounded-full transition-all duration-1000 ease-out"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
        
        {/* Progress Percentage */}
        <div className="absolute -top-8 left-0 right-0 flex justify-center">
          <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
            {Math.round(progressPercentage)}% Complete
          </span>
        </div>
      </div>
      
      {/* Delivery Stages */}
      <div className="grid grid-cols-5 gap-2">
        {deliveryStages.map((stage, index) => {
          const Icon = stage.icon;
          const isCompleted = index <= currentStageIndex;
          const isCurrent = index === currentStageIndex;
          const stageDate = getStageDate(stage.codes);
          
          return (
            <div key={stage.id} className="flex flex-col items-center text-center">
              {/* Stage Icon */}
              <div className={`
                relative w-12 h-12 rounded-full flex items-center justify-center mb-3 transition-all duration-300
                ${isCompleted 
                  ? `bg-${stage.color}-500 text-white shadow-lg` 
                  : 'bg-gray-200 text-gray-400'
                }
                ${isCurrent ? 'ring-4 ring-blue-200 scale-110' : ''}
              `}>
                {isCompleted ? (
                  isCurrent ? <Icon className="text-sm" /> : <FaCheck className="text-sm" />
                ) : (
                  <Icon className="text-sm" />
                )}
                
                {/* Pulse animation for current stage */}
                {isCurrent && (
                  <div className="absolute inset-0 rounded-full bg-blue-500 animate-ping opacity-20"></div>
                )}
              </div>
              
              {/* Stage Label */}
              <div className={`text-xs font-medium mb-1 ${
                isCompleted ? 'text-gray-800' : 'text-gray-500'
              }`}>
                {stage.label}
              </div>
              
              {/* Stage Date */}
              {stageDate && (
                <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                  {stageDate}
                </div>
              )}
              
              {/* Current Stage Indicator */}
              {isCurrent && (
                <div className="mt-2 w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
              )}
            </div>
          );
        })}
      </div>
      
      {/* Tooltip for more details */}
      <div className="mt-6 text-center">
        <p className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 inline-block">
          💡 Detailed shipment tracking information is available in the sections below
        </p>
      </div>
    </div>
  );
};

export default DeliveryProgressBar;